import {getRetailLocationTemplatesFromWorkspaceCapability, RetailLocationTemplateStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/retailLocationsWorkspaceCapability/domain/RetailLocationTemplate";
import {getWorkspaceCapabilityOwnedUdoTemplateIds} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {getEventAdditionalColumns, getEventContainerAdditionalColumns} from "~/utilities/capabilityUdoTemplateColumns";
import {createFileRoute, notFound, redirect} from "@tanstack/react-router";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {UserAccessLevel} from "~/typeDefinitions";
import {
    CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
    getAllWorkspaceUserDetailsWithCaching,
    getEnabledWorkspaceUiConfigWithCaching,
    getDomainAggregateTemplatesWithCaching,
    getPlatformAccessMatrixWithCaching,
    getOrganizationalUnitsWithCaching,
    getOrganizationalUnitTemplatesWithCaching,
    getProductCataloguesWithCaching,
    getProductCatalogueTemplatesWithCaching,
    getUserTemplatesWithCaching,
    getWorkspaceUsersWithCaching,
    getWorkspaceCapabilitiesWithCaching,
    getWorkspaceAccessMatrixWithCaching,
    getWorkspaceCapabilityAccessMatrixWithCaching,
    getUserDefinedObjectsWithCaching,
    getUserDefinedObjectTemplatesWithCaching,
} from "~/utilities/queries";
import {WORKSPACE__SUBSCRIPTION_DETAILS__QUERY_KEY} from "~/utilities/queryKeys";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {type WorkspaceCapability, WorkspaceCapabilityStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {dataResult} from "@vesta/error-handling-remix/result";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {ProductCatalogueTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogueTemplate";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {UserStatus} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {z as zod} from "zod";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch.ts";
import {
    type SubscriptionDetails,
    subscriptionDetailsZodValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/Subscription";
import {
    type PricingPlanNudgeStatus,
    pricingPlanNudgeStatusZodValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PricingPlanNudgeStatus";
import {getOttopilotEventTemplatesFromWorkspaceCapability, OttopilotEventTemplateStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {DomainAggregateTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/domainAggregateWorkspaceCapability/domain/DomainAggregateTemplate";
import {getOttopilotEventContainerTemplatesFromWorkspaceCapability, OttopilotEventContainerTemplateStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplate";
import type {UserDetails} from "@vesta/vos-types/modules/platformLevel/userDetails/domain/UserDetails.ts";
import {setWorkspaceConfigFromUiConfig} from "~/workspaceConfig/configRuntime";
import {UserDefinedObjectTemplateStatus, type UserDefinedObjectTemplate} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import type {UserDefinedObject} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObject";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {UserAdditionalColumnType} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplateAdditionalColumns.ts";
import {OrganizationalUnitTemplateAdditionalColumnType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplateAdditionalColumns.ts";
import {ProductCatalogueTemplateAdditionalColumnType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogueTemplateAdditionalColumns.ts";
import {UserDefinedObjectTemplateAdditionalColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {hasDomainAggregateReadAccess} from "~/modules/domainAggregates/domainAggregateAccess";
import {WorkspaceAccessOperation} from "@vesta/vos-types/modules/workspaceLevel/workspaceAccess/domain/WorkspaceAccessOperation";

export const Route = createFileRoute("/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId")({
    beforeLoad: async ({context, params}) => {
        const workspaceIdResult = uuidZodValidator.safeParse(params.workspaceId);
        if (workspaceIdResult.success !== true) {
            // TODO: What exactly should we do here?
            throw notFound();
        }
        const workspaceId = workspaceIdResult.data;

        const workspace = context.accessibleWorkspaces.find((workspace) => workspace.id === workspaceId);
        if (workspace === undefined) {
            throw notFound();
        }

        const [
            workspaceCapabilitiesResult,
            workspaceUiConfigResult,
            platformUserDetailsResult,
            platformAccessMatrixResult,
            workspaceAccessMatrixResult,
            workspaceCapabilityAccessMatrixResult,
        ] = await Promise.all([
            getWorkspaceCapabilitiesWithCaching({
                workspaceId: workspace.id,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
            }),
            getEnabledWorkspaceUiConfigWithCaching({
                workspaceId: workspace.id,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
            }),
            getAllWorkspaceUserDetailsWithCaching({
                workspaceId: workspace.id,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
            }),
            getPlatformAccessMatrixWithCaching({
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
                intellsysBaseUrl: context.intellsysBaseUrl,
            }),
            getWorkspaceAccessMatrixWithCaching({
                workspaceId: workspace.id,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
                intellsysBaseUrl: context.intellsysBaseUrl,
            }),
            getWorkspaceCapabilityAccessMatrixWithCaching({
                workspaceId: workspace.id,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
                intellsysBaseUrl: context.intellsysBaseUrl,
            }),
        ]);

        if (workspaceCapabilitiesResult.success === false) {
            throw workspaceCapabilitiesResult.error;
        }
        if (workspaceUiConfigResult.success === false) {
            throw workspaceUiConfigResult.error;
        }
        if (platformUserDetailsResult.success === false) {
            throw platformUserDetailsResult.error;
        }
        if (platformAccessMatrixResult.success === false) {
            throw platformAccessMatrixResult.error;
        }
        if (workspaceAccessMatrixResult.success === false) {
            throw workspaceAccessMatrixResult.error;
        }
        if (workspaceCapabilityAccessMatrixResult.success === false) {
            throw workspaceCapabilityAccessMatrixResult.error;
        }

        const workspaceCapabilities = workspaceCapabilitiesResult.data;
        // Config-driven pages load from DB workspace_ui_config only.
        setWorkspaceConfigFromUiConfig(workspace.id, workspaceUiConfigResult.data?.configuration ?? null);
        const hasCapability = (type: WorkspaceCapabilityType): boolean =>
            workspaceCapabilities.some((capability) => capability.type === type && capability.status === WorkspaceCapabilityStatus.enabled);
        const canReadUserDefinedObjects = workspaceAccessMatrixResult.data[WorkspaceAccessOperation.readUserDefinedObject as Uuid] === true;
        const canReadDomainAggregates = hasDomainAggregateReadAccess({
            workspaceCapabilities: workspaceCapabilities,
            workspaceCapabilityAccessMatrix: workspaceCapabilityAccessMatrixResult.data,
        });

        // TODO: Should we return null for the capabilities that are not enabled
        // instead of an empty array?
        const [
            userTemplatesResult,
            usersResult,
            domainAggregateTemplatesResult,
            organizationalUnitTemplatesResult,
            organizationalUnitsResult,
            productCatalogueTemplatesResult,
            productCatalogueResult,
            userDefinedObjectTemplatesResult,
        ] = await Promise.all([
            getUserTemplatesWithCaching({
                      workspaceId: workspace.id,
                      accessToken: context.authenticationStatus.accessToken,
                      queryClient: context.queryClient,
                      intellsysBaseUrl: context.intellsysBaseUrl,
                  }),
            getWorkspaceUsersWithCaching({
                      workspaceId: workspace.id,
                      intellsysBaseUrl: context.intellsysBaseUrl,
                      accessToken: context.authenticationStatus.accessToken,
                      queryClient: context.queryClient,
                      ...CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
                  }),
            canReadDomainAggregates
                ? getDomainAggregateTemplatesWithCaching({
                      workspaceId: workspace.id,
                      accessToken: context.authenticationStatus.accessToken,
                      queryClient: context.queryClient,
                      intellsysBaseUrl: context.intellsysBaseUrl,
                  })
                : Promise.resolve(dataResult([])),
            hasCapability(WorkspaceCapabilityType.organizationalUnitTemplatesAndOrganizationalUnits)
                ? getOrganizationalUnitTemplatesWithCaching({
                      workspaceId: workspace.id,
                      accessToken: context.authenticationStatus.accessToken,
                      queryClient: context.queryClient,
                      intellsysBaseUrl: context.intellsysBaseUrl,
                  })
                : Promise.resolve(dataResult([])),
            hasCapability(WorkspaceCapabilityType.organizationalUnitTemplatesAndOrganizationalUnits)
                ? getOrganizationalUnitsWithCaching({
                      workspaceId: workspace.id,
                      accessToken: context.authenticationStatus.accessToken,
                      queryClient: context.queryClient,
                      intellsysBaseUrl: context.intellsysBaseUrl,
                      ...CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
                  })
                : Promise.resolve(dataResult([])),
            hasCapability(WorkspaceCapabilityType.productCatalogueTemplatesAndProductCatalogues)
                ? getProductCatalogueTemplatesWithCaching({
                      workspaceId: workspace.id,
                      intellsysBaseUrl: context.intellsysBaseUrl,
                      accessToken: context.authenticationStatus.accessToken,
                      queryClient: context.queryClient,
                  })
                : Promise.resolve(dataResult([])),
            hasCapability(WorkspaceCapabilityType.productCatalogueTemplatesAndProductCatalogues)
                ? getProductCataloguesWithCaching({
                      workspaceId: workspace.id,
                      intellsysBaseUrl: context.intellsysBaseUrl,
                      accessToken: context.authenticationStatus.accessToken,
                      queryClient: context.queryClient,
                      ...CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
                  })
                : Promise.resolve(dataResult([])),
            canReadUserDefinedObjects
                ? getUserDefinedObjectTemplatesWithCaching({
                      includeArchived: true,
                      workspaceId: workspace.id,
                      accessToken: context.authenticationStatus.accessToken,
                      queryClient: context.queryClient,
                      intellsysBaseUrl: context.intellsysBaseUrl,
                  })
                : Promise.resolve(dataResult([])),
        ]);

        if (userTemplatesResult.success === false) {
            throw userTemplatesResult.error;
        }
        if (usersResult.success === false) {
            throw usersResult.error;
        }
        if (domainAggregateTemplatesResult.success === false) {
            throw domainAggregateTemplatesResult.error;
        }
        if (organizationalUnitTemplatesResult.success === false) {
            throw organizationalUnitTemplatesResult.error;
        }
        if (organizationalUnitsResult.success === false) {
            throw organizationalUnitsResult.error;
        }
        if (productCatalogueTemplatesResult.success === false) {
            throw productCatalogueTemplatesResult.error;
        }
        if (productCatalogueResult.success === false) {
            throw productCatalogueResult.error;
        }
        if (userDefinedObjectTemplatesResult.success === false) {
            throw userDefinedObjectTemplatesResult.error;
        }

        // TODO: Implement these correctly.
        const userAccessLevelOnWorkspace: UserAccessLevel = UserAccessLevel.owner;
        const userTemplates: Array<UserTemplate> = userTemplatesResult.data;
        const domainAggregateTemplates: Array<DomainAggregateTemplate> = domainAggregateTemplatesResult.data;
        const organizationalUnitTemplates: Array<OrganizationalUnitTemplate> = organizationalUnitTemplatesResult.data;
        const productCatalogueTemplates: Array<ProductCatalogueTemplate> = productCatalogueTemplatesResult.data;
        const organizationalUnits: Array<OrganizationalUnit> = organizationalUnitsResult.data;
        const productCatalogue: Array<ProductCatalogue> = productCatalogueResult.data;
        const users: Array<User> = usersResult.data;

        // #region User Defined Objects
        const allUdoTemplates = userDefinedObjectTemplatesResult.data;
        const userDefinedObjectTemplates: Array<UserDefinedObjectTemplate> = allUdoTemplates.filter(
            template => template.status === UserDefinedObjectTemplateStatus.active,
        );
        const eventContainerCapability = workspaceCapabilities.find((capability): capability is WorkspaceCapability<WorkspaceCapabilityType.ottopilotEventContainers> => capability.type === WorkspaceCapabilityType.ottopilotEventContainers);
        const eventsCapability = workspaceCapabilities.find((capability): capability is WorkspaceCapability<WorkspaceCapabilityType.ottopilotEvents> => capability.type === WorkspaceCapabilityType.ottopilotEvents);
        const eventContainerTemplates = canReadUserDefinedObjects && eventContainerCapability?.status === WorkspaceCapabilityStatus.enabled
            ? getOttopilotEventContainerTemplatesFromWorkspaceCapability({workspaceCapability: eventContainerCapability, allUdoTemplates})
                .filter(template => template.status === OttopilotEventContainerTemplateStatus.active)
            : [];
        const eventTemplates = canReadUserDefinedObjects && eventsCapability?.status === WorkspaceCapabilityStatus.enabled
            ? getOttopilotEventTemplatesFromWorkspaceCapability({workspaceCapability: eventsCapability, allUdoTemplates})
                .filter(template => template.status === OttopilotEventTemplateStatus.active)
            : [];
        const retailLocationCapability = workspaceCapabilities.find((capability): capability is WorkspaceCapability<WorkspaceCapabilityType.retailLocations> => capability.type === WorkspaceCapabilityType.retailLocations);
        // Compose with archived metadata before filtering, so valid archived bindings do not look missing.
        const retailLocationTemplates = canReadUserDefinedObjects && retailLocationCapability?.status === WorkspaceCapabilityStatus.enabled
            ? getRetailLocationTemplatesFromWorkspaceCapability({workspaceCapability: retailLocationCapability, allUdoTemplates})
                .filter(template => template.status === RetailLocationTemplateStatus.active)
            : [];
        const referencedUserDefinedObjectTemplateIds = new Set<Uuid>();
        for (const userTemplate of userTemplates) {
            for (const column of userTemplate.additionalColumns) {
                if (column.type === UserAdditionalColumnType.userDefinedObject || column.type === UserAdditionalColumnType.userDefinedObjects) {
                    referencedUserDefinedObjectTemplateIds.add(column.configuration.userDefinedObjectTemplateId as Uuid);
                }
            }
        }
        for (const organizationalUnitTemplate of organizationalUnitTemplates) {
            for (const column of organizationalUnitTemplate.additionalColumns) {
                if (column.type === OrganizationalUnitTemplateAdditionalColumnType.userDefinedObject || column.type === OrganizationalUnitTemplateAdditionalColumnType.userDefinedObjects) {
                    referencedUserDefinedObjectTemplateIds.add(column.configuration.userDefinedObjectTemplateId as Uuid);
                }
            }
        }
        for (const productCatalogueTemplate of productCatalogueTemplates) {
            for (const column of productCatalogueTemplate.additionalColumns) {
                if (column.type === ProductCatalogueTemplateAdditionalColumnType.userDefinedObject || column.type === ProductCatalogueTemplateAdditionalColumnType.userDefinedObjects) {
                    referencedUserDefinedObjectTemplateIds.add(column.configuration.userDefinedObjectTemplateId as Uuid);
                }
            }
        }
        for (const eventContainerTemplate of eventContainerTemplates) {
            for (const column of getEventContainerAdditionalColumns({workspaceId: workspace.id, template: eventContainerTemplate, udoTemplates: allUdoTemplates})) {
                if (column.type === UserDefinedObjectTemplateAdditionalColumnType.userDefinedObject || column.type === UserDefinedObjectTemplateAdditionalColumnType.userDefinedObjects) {
                    referencedUserDefinedObjectTemplateIds.add(column.configuration.userDefinedObjectTemplateId as Uuid);
                }
            }
        }
        for (const eventTemplate of eventTemplates) {
            for (const column of getEventAdditionalColumns({workspaceId: workspace.id, template: eventTemplate, udoTemplates: allUdoTemplates})) {
                if (column.type === UserDefinedObjectTemplateAdditionalColumnType.userDefinedObject || column.type === UserDefinedObjectTemplateAdditionalColumnType.userDefinedObjects) {
                    referencedUserDefinedObjectTemplateIds.add(column.configuration.userDefinedObjectTemplateId as Uuid);
                }
            }
        }
        const ownedUdoTemplateIds = getWorkspaceCapabilityOwnedUdoTemplateIds(workspaceCapabilities);
        const referencedUserDefinedObjectTemplates = userDefinedObjectTemplates.filter((template) =>
            referencedUserDefinedObjectTemplateIds.has(template.id) && !ownedUdoTemplateIds.has(template.id.toLowerCase() as Uuid)
        );
        const userDefinedObjectResults = await Promise.all(referencedUserDefinedObjectTemplates.map((template) => getUserDefinedObjectsWithCaching({
            workspaceId: workspace.id,
            accessToken: context.authenticationStatus.accessToken,
            queryClient: context.queryClient,
            intellsysBaseUrl: context.intellsysBaseUrl,
            templateId: template.id,
        })));
        const failedUserDefinedObjectResult = userDefinedObjectResults.find((result) => result.success === false);
        if (failedUserDefinedObjectResult?.success === false) {
            throw failedUserDefinedObjectResult.error;
        }
        const userDefinedObjects: Array<UserDefinedObject> = userDefinedObjectResults.flatMap((result) => result.success === true ? result.data : []);
        const userDefinedObjectOptionsByTemplateId = new Map<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>(
            referencedUserDefinedObjectTemplates.map((template) => [
                template.id,
                userDefinedObjects
                    .filter((object) => object.templateId === template.id)
                    .map((object) => ({
                        displayName: processOttopilotStringTemplate(template.instanceDisplayNameTemplate, object.additionalColumnValues),
                        id: object.id,
                    })),
            ]),
        );

        // #endregion User Defined Objects

        const currentWorkspaceUser = users.find((user) => user.userId === context.profileStatus.platformUser.userId);
        if (currentWorkspaceUser === undefined) {
            throw new Error("Current workspace user not found for platform user " + context.profileStatus.platformUser.userId);
        }
        const platformUserDetailsByUserId = new Map<Uuid, UserDetails>(
            platformUserDetailsResult.data.map((platformUser) => [platformUser.userId, platformUser]),
        );
        platformUserDetailsByUserId.set(context.profileStatus.platformUser.userId, context.profileStatus.platformUser);
        const platformUserDetails: Array<UserDetails> = Array.from(platformUserDetailsByUserId.values());

        if (currentWorkspaceUser.status !== UserStatus.active) {
            throw redirect({
                to: "/u/select-workspace",
            });
        }

        const {pricingPlanNudgeStatus, subscriptionDetails} = await context.queryClient.ensureQueryData<{
            pricingPlanNudgeStatus: PricingPlanNudgeStatus | null;
            subscriptionDetails: SubscriptionDetails;
        }>({
            queryKey: [WORKSPACE__SUBSCRIPTION_DETAILS__QUERY_KEY, workspace.id],
            queryFn: async () => {
                const companyDetailsResult = await robustJsonResultClientFetch(
                    `${context.intellsysBaseUrl}/sub-system/pluto/api/ottosync-subscription/get-company-details`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${context.authenticationStatus.accessToken}`,
                            "Content-Type": "application/json",
                            "X-Workspace-Id": workspace.id,
                        },
                        body: JSON.stringify({}),
                    },
                    zod.object({
                        pricingPlanNudgeStatus: pricingPlanNudgeStatusZodValidator.nullable(),
                        subscriptionDetails: subscriptionDetailsZodValidator,
                    }),
                );
                if (companyDetailsResult.success === false) {
                    throw companyDetailsResult.error;
                }
                const companyDetails = companyDetailsResult.data;

                return companyDetails;
            },
            retry: false,
            staleTime: Infinity,
        });

        return {
            workspace: workspace,
            pricingPlanNudgeStatus: pricingPlanNudgeStatus,
            userAccessLevelOnWorkspace: userAccessLevelOnWorkspace,
            users: users,
            userTemplates: userTemplates,
            eventContainerTemplates: eventContainerTemplates,
            eventTemplates: eventTemplates,
            retailLocationTemplates: retailLocationTemplates,
            domainAggregateTemplates: domainAggregateTemplates,
            organizationalUnitTemplates: organizationalUnitTemplates,
            workspaceCapabilities: workspaceCapabilities,
            workspaceUiConfig: workspaceUiConfigResult.data ?? undefined,
            productCatalogueTemplates: productCatalogueTemplates,
            organizationalUnits: organizationalUnits,
            productCatalogue: productCatalogue,
            currentWorkspaceUser: currentWorkspaceUser,
            subscriptionDetails: subscriptionDetails,
            platformUserDetails: platformUserDetails,
            platformAccessMatrix: platformAccessMatrixResult.data,
            workspaceAccessMatrix: workspaceAccessMatrixResult.data,
            workspaceCapabilityAccessMatrix: workspaceCapabilityAccessMatrixResult.data,
            userDefinedObjects: userDefinedObjects,
            userDefinedObjectTemplates: userDefinedObjectTemplates,
            userDefinedObjectOptionsByTemplateId: userDefinedObjectOptionsByTemplateId,
        };
    },
    loader: async ({context}) => {
        return {
            workspace: context.workspace,
            accessibleWorkspaces: context.accessibleWorkspaces,
            userTemplates: context.userTemplates,
            eventContainerTemplates: context.eventContainerTemplates,
            eventTemplates: context.eventTemplates,
            retailLocationTemplates: context.retailLocationTemplates,
            domainAggregateTemplates: context.domainAggregateTemplates,
            organizationalUnitTemplates: context.organizationalUnitTemplates,
            workspaceCapabilities: context.workspaceCapabilities,
            workspaceUiConfig: context.workspaceUiConfig,
            productCatalogueTemplates: context.productCatalogueTemplates,
            organizationalUnits: context.organizationalUnits,
            productCatalogue: context.productCatalogue,
            users: context.users,
            currentWorkspaceUser: context.currentWorkspaceUser,
            platformUserDetails: context.platformUserDetails,
            platformAccessMatrix: context.platformAccessMatrix,
            workspaceAccessMatrix: context.workspaceAccessMatrix,
            workspaceCapabilityAccessMatrix: context.workspaceCapabilityAccessMatrix,
            userDefinedObjects: context.userDefinedObjects,
            userDefinedObjectTemplates: context.userDefinedObjectTemplates,
            userDefinedObjectOptionsByTemplateId: context.userDefinedObjectOptionsByTemplateId,
        };
    },
    // Child page changes must not reload this layout. defaultStaleTime/pendingMs are 0,
    // so a reload would flash pendingComponent and unmount the sidebar.
    staleTime: Infinity,
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
